// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import {
  Package,
  Palette,
  DollarSign,
  Calendar,
  TrendingUp,
  Users,
  MessageCircle,
  CreditCard,
  Loader2,
  Plus,
  Eye,
  Clock,
  CheckCircle
} from 'lucide-react';

interface GraphicsVendorProfile {
  id: string;
  user_id: string;
  business_name?: string;
  description?: string;
  rating_average?: number;
  review_count?: number;
  created_at: string;
  updated_at: string;
}

interface DashboardStats {
  totalOrders: number;
  pendingOrders: number;
  completedOrders: number;
  totalRevenue: number;
  totalPackages: number;
  averageOrderValue: number;
  messageCount: number;
  totalEarnings: number;
}

const VendorGraphicsDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [vendorProfile, setVendorProfile] = useState<GraphicsVendorProfile | null>(null);
  const [stats, setStats] = useState<DashboardStats>({
    totalOrders: 0,
    pendingOrders: 0,
    completedOrders: 0,
    totalRevenue: 0,
    totalPackages: 0,
    averageOrderValue: 0,
    messageCount: 0,
    totalEarnings: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, [user?.id]);

  const fetchDashboardData = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);

      // Get vendor profile
      const { data: profileData, error: profileError } = await supabase
        .from('graphics_vendor_profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (profileError) throw profileError;
      setVendorProfile(profileData);

      if (profileData?.id) {
        // Get orders statistics
        const { data: orders, error: ordersError } = await supabase
          .from('graphics_orders')
          .select('status, total_amount')
          .eq('vendor_id', profileData.id);

        if (ordersError) throw ordersError;

        // Get packages count
        const { count: packagesCount, error: packagesError } = await supabase
          .from('graphics_packages')
          .select('*', { count: 'exact', head: true })
          .eq('vendor_id', profileData.id);

        if (packagesError) throw packagesError;

        // Get message count from graphics_messages for this vendor's orders
        let messageCount = 0;
        if (orders && orders.length > 0) {
          const orderIds = orders.map((_, index) => {
            // Get the order IDs by querying graphics_orders again
            return supabase
              .from('graphics_orders')
              .select('id')
              .eq('vendor_id', profileData.id);
          });
          
          // Simpler approach: get message count by joining with orders
          const { data: vendorOrders } = await supabase
            .from('graphics_orders')
            .select('id')
            .eq('vendor_id', profileData.id);
          
          if (vendorOrders && vendorOrders.length > 0) {
            const orderIdList = vendorOrders.map(order => order.id);
            const { count: msgCount } = await supabase
              .from('graphics_messages')
              .select('*', { count: 'exact', head: true })
              .in('order_id', orderIdList);
            
            messageCount = msgCount || 0;
          }
        }

        // Get actual earnings from graphics_vendor_earnings
        const { data: earnings, error: earningsError } = await supabase
          .from('graphics_vendor_earnings')
          .select('amount')
          .eq('vendor_id', profileData.id);

        if (earningsError) throw earningsError;

        // Calculate statistics
        const totalOrders = orders?.length || 0;
        const pendingOrders = orders?.filter(o => o.status === 'pending').length || 0;
        const completedOrders = orders?.filter(o => o.status === 'completed').length || 0;
        const totalRevenue = orders?.reduce((sum, order) => sum + (order.total_amount || 0), 0) || 0;
        const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
        const totalEarnings = earnings?.reduce((sum, earning) => sum + (earning.amount || 0), 0) || 0;

        setStats({
          totalOrders,
          pendingOrders,
          completedOrders,
          totalRevenue: totalRevenue, // Already in dollars
          totalPackages: packagesCount || 0,
          averageOrderValue: averageOrderValue, // Already in dollars
          messageCount: messageCount || 0,
          totalEarnings: totalEarnings,
        });
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (amount: number) => {
    return amount.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin mr-2" />
        <span>Loading dashboard...</span>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Graphics Dashboard</h1>
          <p className="text-muted-foreground">
            Welcome back{vendorProfile?.business_name ? `, ${vendorProfile.business_name}` : ''}! 
            Here's what's happening with your graphics business.
          </p>
        </div>
        {!vendorProfile && (
          <Button onClick={() => navigate('/vendor-graphics-dashboard/profile')}>
            <Plus className="h-4 w-4 mr-2" />
            Complete Profile
          </Button>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Orders</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalOrders}</div>
            <p className="text-xs text-muted-foreground">
              +{stats.pendingOrders} pending
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatPrice(stats.totalRevenue)}</div>
            <p className="text-xs text-muted-foreground">
              Avg: {formatPrice(stats.averageOrderValue)} per order
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Packages</CardTitle>
            <Palette className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalPackages}</div>
            <p className="text-xs text-muted-foreground">
              Graphics service packages
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/vendor-graphics-dashboard/orders')}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Orders</CardTitle>
            <Clock className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.pendingOrders}</div>
            <Button variant="ghost" size="sm" className="p-0 h-auto font-normal text-xs">
              <Eye className="h-3 w-3 mr-1" />
              View all orders
            </Button>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/vendor-graphics-dashboard/packages')}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Manage Packages</CardTitle>
            <Package className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalPackages}</div>
            <Button variant="ghost" size="sm" className="p-0 h-auto font-normal text-xs">
              <Plus className="h-3 w-3 mr-1" />
              Add new package
            </Button>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/vendor-graphics-dashboard/messages')}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Messages</CardTitle>
            <MessageCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.messageCount}</div>
            <Button variant="ghost" size="sm" className="p-0 h-auto font-normal text-xs">
              <Eye className="h-3 w-3 mr-1" />
              Check messages
            </Button>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/vendor-graphics-dashboard/finance')}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Earnings</CardTitle>
            <CreditCard className="h-4 w-4 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatPrice(stats.totalEarnings)}</div>
            <Button variant="ghost" size="sm" className="p-0 h-auto font-normal text-xs">
              <TrendingUp className="h-3 w-3 mr-1" />
              View finance
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Profile Status */}
      {vendorProfile ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              Your Graphics Business Profile
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="font-medium">Business Name:</span>
                <span>{vendorProfile.business_name || 'Not set'}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Rating:</span>
                <span>{vendorProfile.rating_average ? `${vendorProfile.rating_average}/5.0` : 'No ratings yet'}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Reviews:</span>
                <span>{vendorProfile.review_count || 0} reviews</span>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <Button variant="outline" onClick={() => navigate('/vendor-graphics-dashboard/profile')}>
                Edit Profile
              </Button>
              <Button variant="outline" onClick={() => navigate('/vendor-graphics-dashboard/portfolio')}>
                Manage Portfolio
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-yellow-500" />
              Complete Your Graphics Profile
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">
              Set up your graphics business profile to start receiving orders from customers.
            </p>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                <span>Add business information</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                <span>Create your first graphics package</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                <span>Upload portfolio samples</span>
              </div>
            </div>
            <Button className="mt-4" onClick={() => navigate('/vendor-graphics-dashboard/profile')}>
              Get Started
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default VendorGraphicsDashboard;